import { deploymentConfig } from "./deploymentConfig";
import {
  extractFingerprint,
  hasDeploymentChanged,
  isFingerprintUsable,
} from "./fingerprint";
import { devWarn } from "./utils/logger";
import type { DeploymentFingerprint } from "./types";

export type ProbeOutcome =
  | { outcome: "success"; fingerprint: DeploymentFingerprint }
  | { outcome: "failed" }
  | { outcome: "aborted" }
  | { outcome: "unusable-fingerprint" };

/** Performs a lightweight HEAD probe against the configured check URL. */
export async function probeDeployment(
  signal?: AbortSignal,
  checkUrl: string = deploymentConfig.checkUrl,
): Promise<ProbeOutcome> {
  try {
    const response = await fetch(checkUrl, {
      method: "HEAD",
      cache: "no-store",
      signal,
    });

    if (!response.ok) {
      return { outcome: "failed" };
    }

    const fingerprint = extractFingerprint(response);

    if (!isFingerprintUsable(fingerprint)) {
      devWarn(
        "HEAD response missing ETag and Last-Modified — deployment checks inactive for this response.",
        { checkUrl, status: response.status },
      );
      return { outcome: "unusable-fingerprint" };
    }

    return { outcome: "success", fingerprint };
  } catch (error) {
    if (signal?.aborted) {
      return { outcome: "aborted" };
    }

    devWarn("Deployment probe failed — will retry on next trigger.", error);
    return { outcome: "failed" };
  }
}

/** Returns true when the live fingerprint differs from the running baseline. */
export function isDeploymentChanged(
  baseline: DeploymentFingerprint,
  current: DeploymentFingerprint,
): boolean {
  return hasDeploymentChanged(baseline, current);
}

/**
 * Navigate to the current URL, replacing the history entry.
 * Avoids leaving a stale history step the user can navigate back to after an update.
 */
export function reloadApplication(): void {
  window.location.replace(window.location.href);
}
